import React from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { UploadCloud, CheckCircle2, XCircle } from 'lucide-react';
import { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { ROLES } from '../mock';

const DOMAIN = 'aktawealth.com';

const parseEmails = (raw) => {
  return raw
    .split(/[\s,;\n]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
};

export const BulkInviteButton = ({ users, onDone }) => {
  const [open, setOpen] = React.useState(false);
  const [raw, setRaw] = React.useState('');
  const [role, setRole] = React.useState(ROLES.RM);
  const [salesHeadId, setSalesHeadId] = React.useState('');
  const [selectedRmIds, setSelectedRmIds] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [results, setResults] = React.useState(null);

  const salesHeads = (users || []).filter((u) => u.role === ROLES.SALES_HEAD);
  const rms = (users || []).filter((u) => u.role === ROLES.RM);

  const parsed = parseEmails(raw);
  const invalid = parsed.filter((e) => !e.endsWith(`@${DOMAIN}`));
  const valid = parsed.filter((e) => e.endsWith(`@${DOMAIN}`));

  const reset = () => {
    setRaw(''); setRole(ROLES.RM); setSalesHeadId(''); setSelectedRmIds([]); setResults(null);
  };

  const submit = async () => {
    if (valid.length === 0) { toast({ title: 'No valid @' + DOMAIN + ' emails detected' }); return; }
    setBusy(true); setResults(null);
    try {
      const rows = valid.map((email) => {
        const row = { email, role };
        if (role === ROLES.RM && salesHeadId) row.sales_head_id = salesHeadId;
        if (role === ROLES.SALES_HEAD && selectedRmIds.length) row.rm_ids = selectedRmIds;
        return row;
      });
      const r = await fetch(`${API}/users/bulk`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ users: rows }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Bulk invite failed');
      setResults(j);
      toast({ title: `Bulk invite complete`, description: `${j.created} created · ${j.skipped} skipped` });
      if (typeof onDone === 'function') onDone();
    } catch (e) {
      toast({ title: 'Bulk invite failed', description: e.message });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-brand-border text-brand-indigo">
          <UploadCloud className="h-4 w-4 mr-2" /> Bulk invite
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle>Bulk invite users</DialogTitle></DialogHeader>

        {!results ? (
          <div className="space-y-4 overflow-y-auto">
            <div>
              <Label>Paste one email per line (or comma-separated)</Label>
              <Textarea
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                className="mt-1 min-h-[140px] font-mono text-sm"
                placeholder={`priya@${DOMAIN}\nrajesh@${DOMAIN}\nsonal@${DOMAIN}`}
              />
              <div className="mt-2 flex items-center gap-3 text-[11px]">
                <span className="text-brand-green font-semibold">{valid.length} valid</span>
                {invalid.length > 0 && <span className="text-brand-red font-semibold">{invalid.length} invalid (not @{DOMAIN})</span>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Assign role</Label>
                <Select value={role} onValueChange={(v) => { setRole(v); setSelectedRmIds([]); setSalesHeadId(''); }}>
                  <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ROLES.ADMIN}>Admin</SelectItem>
                    <SelectItem value={ROLES.SALES_HEAD}>Sales Head</SelectItem>
                    <SelectItem value={ROLES.RM}>Relationship Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {role === ROLES.RM && (
                <div>
                  <Label>Reports to</Label>
                  <Select value={salesHeadId} onValueChange={setSalesHeadId}>
                    <SelectTrigger className="mt-1 h-10"><SelectValue placeholder="Optional — pick Sales Head" /></SelectTrigger>
                    <SelectContent>
                      {salesHeads.map((sh) => (
                        <SelectItem key={sh.user_id} value={sh.user_id}>{sh.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {role === ROLES.SALES_HEAD && (
              <div>
                <Label>Map these Sales Heads to RMs (optional)</Label>
                <div className="mt-1 border border-brand-border rounded-md p-2 max-h-32 overflow-y-auto">
                  {rms.length === 0 && <div className="text-xs text-muted-foreground p-2">No RMs yet.</div>}
                  {rms.map((rm) => {
                    const checked = selectedRmIds.includes(rm.user_id);
                    return (
                      <label key={rm.user_id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-brand-bg cursor-pointer">
                        <Checkbox checked={checked} onCheckedChange={(v) => setSelectedRmIds(v ? [...selectedRmIds, rm.user_id] : selectedRmIds.filter((x) => x !== rm.user_id))} />
                        <div className="text-sm text-brand-indigo">{rm.name} <span className="text-[11px] text-muted-foreground">· {rm.email}</span></div>
                      </label>
                    );
                  })}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Same mapping applied to every Sales Head created in this batch. Edit individually later if needed.</div>
              </div>
            )}

            <div className="text-[11px] text-muted-foreground border border-brand-border rounded-md p-2 bg-brand-bg">
              Names are auto-derived from the email prefix (e.g. <code>priya.shah@{DOMAIN}</code> → &ldquo;Priya Shah&rdquo;). Edit individual users after the batch if needed.
            </div>
          </div>
        ) : (
          <div className="space-y-2 overflow-y-auto">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-brand-green font-semibold">{results.created} created</span>
              <span className="text-brand-red font-semibold">{results.skipped} skipped</span>
            </div>
            <div className="max-h-[300px] overflow-y-auto border border-brand-border rounded-md divide-y divide-brand-border/60">
              {results.results.map((r, i) => (
                <div key={i} className="px-3 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    {r.status === 'created' ? <CheckCircle2 className="h-4 w-4 text-brand-green shrink-0" /> : <XCircle className="h-4 w-4 text-brand-red shrink-0" />}
                    <div className="font-medium text-brand-indigo truncate">{r.email}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground text-right ml-3">
                    {r.status === 'created' ? `Created · ${r.user_id}` : `${r.code} · ${r.detail}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {!results ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy || valid.length === 0} className="bg-brand-indigo text-white">
                {busy ? 'Inviting\u2026' : `Invite ${valid.length} user${valid.length === 1 ? '' : 's'}`}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset}>Invite more</Button>
              <Button onClick={() => setOpen(false)} className="bg-brand-indigo text-white">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkInviteButton;
